import Component from "@base/Component.js";
import api from "@api/api.js";
import config from "@/config.js";
import { loadGoogleMaps, hasMaps } from "@/services/googleMaps.js";
import { lockScroll, unlockScroll } from "@/services/scrollLock.js";
import MapView from "./MapView.js";
import TagInput from "@/components/TagInput.js";
import { uniqueTags } from "@/services/tags.js";
import t from "@/services/translationService.js";

/**
 * Admin create/edit overlay for a gallery. Mount to document.body.
 * props: { gallery: GalleryObject|null, onDone() }
 *
 * Produces the FormData the backend expects (POST /api/post/save/:id):
 * id, date, reverse, type, accessType, en_/de_ title/subTitle/text, mapId,
 * mapPlaceId (JSON), zoomLevel, imagesToKeep (JSON), images (files).
 */
export default class GalleryEditor extends Component {
  constructor(props) {
    super(props);
    const g = props.gallery;
    this.isNew = !g;
    this.galleryId = g?.galleryId || crypto.randomUUID();
    this.existingImages = (g?.images || []).map((i) => i.name);
    this.removed = new Set();
    this.addedThisSession = new Set(); // names uploaded during this edit, for the "new" badge
    // Ordered list of map places; a place's index here is its marker number,
    // both in the list below and in the live preview map (see #refreshPlaceList).
    // Labels start out as the raw place id and are resolved to real names
    // shortly after mount (see #resolveExistingLabels) - existing galleries
    // only ever had ids persisted, never a display name.
    this.places = (g?.placeIds?.() || []).map((placeId) => ({ placeId, label: placeId }));
    this.mapPreview = null;
    this.data = {
      date: (g?.date || new Date().toISOString().slice(0, 10)),
      type: g?.type || "grid",
      accessType: g?.accessType ?? 2,
      zoomLevel: g?.zoomLevel ?? 8,
      mapId: g?.mapId || crypto.randomUUID(),
      en_title: g?.en_title || "",
      en_subTitle: g?.en_subtitle || "",
      en_text: g?.en_content || "",
      de_title: g?.de_title || "",
      de_subTitle: g?.de_subtitle || "",
      de_text: g?.de_content || "",
      tags: uniqueTags(g?.tagList?.() || []),
      titleImage: g?.titleImage || null,
    };
  }

  render() {
    const d = this.data;
    const val = (v) => Component.escape(v);
    const imgs = this.existingImages
      .map((name) => {
        const isTitle = d.titleImage === name;
        const cls = [
          "editor-thumb",
          this.removed.has(name) && "editor-thumb--removed",
          this.addedThisSession.has(name) && "editor-thumb--new",
        ]
          .filter(Boolean)
          .join(" ");
        return `
        <div class="${cls}" data-name="${val(name)}">
          <img src="${config.imageBase}/${encodeURIComponent(this.galleryId)}/${encodeURIComponent(name)}?size=thumb" alt="" loading="lazy" />
          <button type="button" class="editor-thumb__toggle" data-name="${val(name)}">${this.removed.has(name) ? "↺" : "×"}</button>
          <button type="button" class="editor-thumb__star${isTitle ? " editor-thumb__star--active" : ""}" data-name="${val(name)}" title="${val(isTitle ? t.t("cockpit.galleries.editor.titleImage") : t.t("cockpit.galleries.editor.setTitleImage"))}">★</button>
        </div>`;
      })
      .join("");

    return `
      <div class="modal-backdrop">
        <div class="modal-card modal-card--wide">
          <div class="modal-header">
            <h2>${val(this.isNew ? t.t("cockpit.galleries.add") : t.t("cockpit.galleries.editor.edit"))}</h2>
            <button type="button" class="modal-close" aria-label="${val(t.t("common.close"))}">&times;</button>
          </div>
          <form class="editor-form">
            <div class="editor-cols">
              <fieldset>
                <legend>${val(t.t("common.languageEn"))}</legend>
                <label>${val(t.t("cockpit.galleries.editor.title"))}<input name="en_title" value="${val(d.en_title)}" required /></label>
                <label>${val(t.t("cockpit.galleries.editor.subtitle"))}<input name="en_subTitle" value="${val(d.en_subTitle)}" /></label>
                <label>${val(t.t("cockpit.galleries.editor.text"))}<textarea name="en_text" rows="4">${val(d.en_text)}</textarea></label>
              </fieldset>
              <fieldset>
                <legend>${val(t.t("common.languageDe"))}</legend>
                <label>${val(t.t("cockpit.galleries.editor.title"))}<input name="de_title" value="${val(d.de_title)}" required /></label>
                <label>${val(t.t("cockpit.galleries.editor.subtitle"))}<input name="de_subTitle" value="${val(d.de_subTitle)}" /></label>
                <label>${val(t.t("cockpit.galleries.editor.text"))}<textarea name="de_text" rows="4">${val(d.de_text)}</textarea></label>
              </fieldset>
            </div>
            <div class="editor-meta">
              <label>${val(t.t("cockpit.galleries.editor.date"))}<input type="date" name="date" value="${val(d.date)}" /></label>
              <label>${val(t.t("cockpit.galleries.editor.type"))}
                <select name="type">
                  <option value="grid" ${d.type === "grid" ? "selected" : ""}>${val(t.t("cockpit.galleries.editor.typeGrid"))}</option>
                </select>
              </label>
              <label>${val(t.t("cockpit.galleries.editor.zoom"))}<input type="number" name="zoomLevel" value="${val(d.zoomLevel)}" min="1" max="20" /></label>
              <label>${val(t.t("cockpit.galleries.editor.visibility"))}
                <select name="accessType">
                  <option value="2" ${d.accessType === 2 ? "selected" : ""}>${val(t.t("cockpit.visibility.draft"))}</option>
                  <option value="0" ${d.accessType === 0 ? "selected" : ""}>${val(t.t("cockpit.visibility.private"))}</option>
                  <option value="1" ${d.accessType === 1 ? "selected" : ""}>${val(t.t("cockpit.visibility.public"))}</option>
                </select>
              </label>
            </div>
            <label>${val(t.t("cockpit.galleries.colTags"))}</label>
            <div class="tag-input-slot"></div>
            <label>${val(t.t("cockpit.galleries.editor.mapPlaces"))}</label>
            <ol class="place-list"></ol>
            ${hasMaps() ? `<button type="button" class="btn-secondary place-search-toggle">+ ${val(t.t("cockpit.galleries.editor.addPlace"))}</button>` : ""}
            ${
              hasMaps()
                ? `<div class="place-search-popover" hidden>
                     <div class="place-search-input-slot"></div>
                     <p class="place-search-status"></p>
                     <button type="button" class="btn-secondary place-search-done">${val(t.t("cockpit.galleries.editor.placeDone"))}</button>
                   </div>`
                : ""
            }
            <div class="map-preview-slot"></div>

            <div class="editor-images">
              <div class="editor-thumbs">${imgs}</div>
              <label class="editor-file">${val(t.t("cockpit.galleries.editor.addImages"))}<input type="file" name="images" accept=".jpg,.jpeg,.png" multiple /></label>
              <div class="editor-upload-progress" hidden>
                <div class="editor-upload-progress__bar"></div>
              </div>
              <p class="editor-hint">${val(t.t("cockpit.galleries.editor.uploadHint"))}</p>
            </div>

            <div class="form-error" role="alert"></div>
            <div class="editor-actions">
              <button type="submit" class="btn-primary">${val(t.t("common.save"))}</button>
              ${this.isNew ? "" : `<button type="button" class="btn-danger" data-action="delete">${val(t.t("common.delete"))}</button>`}
              <button type="button" class="btn-secondary" data-action="cancel">${val(t.t("common.cancel"))}</button>
            </div>
          </form>
        </div>
      </div>`;
  }

  onMounted() {
    lockScroll();

    const backdrop = this.el;
    const errorEl = this.$(".form-error");
    const close = () => this.unmount();

    this.listen(this.$(".modal-close"), "click", close);
    this.listen(this.$('[data-action="cancel"]'), "click", close);
    this.listen(backdrop, "click", (e) => e.target === backdrop && close());

    // Pressing Enter in any text input (e.g. while adding a tag or searching
    // for a place) would otherwise submit + close the whole form via the
    // browser's default "Enter submits the form" behaviour. Block-list
    // rather than allow-list INPUT specifically: a keydown originating
    // inside PlaceAutocompleteElement's internal shadow-DOM input retargets
    // e.target to the custom element itself, not "INPUT". Textareas keep
    // their normal newline behaviour, and buttons (Save/Cancel/Delete) keep
    // working via keyboard as expected.
    this.listen(this.$(".editor-form"), "keydown", (e) => {
      if (e.key !== "Enter") return;
      if (e.target.tagName !== "TEXTAREA" && e.target.tagName !== "BUTTON") e.preventDefault();
    });

    this.#mountTagInput();

    // Numbered place list: each item's position is its marker number, both
    // in the list itself and in the live preview map below it (see
    // #refreshPlaceList/#refreshMapPreview). Existing galleries only ever
    // persisted raw place ids, so real names are resolved after mount.
    this.#refreshPlaceList();
    this.#resolveExistingLabels();

    // Place search: lets the admin find a location by name instead of having
    // to already know its Google Place ID. Uses the modern
    // PlaceAutocompleteElement (not the legacy Autocomplete widget) - the
    // legacy widget always renders its suggestion dropdown as a
    // `.pac-container` appended to <body> with document-coordinate absolute
    // positioning, which this modal's scroll-lock (position: fixed on
    // <body>, see scrollLock.js) throws off. PlaceAutocompleteElement is a
    // real element we insert ourselves, so - assuming it doesn't *also*
    // escape to <body> internally - it should render correctly without
    // needing any of that.
    const searchToggle = this.$(".place-search-toggle");
    if (searchToggle) {
      const popover = this.$(".place-search-popover");
      const inputSlot = this.$(".place-search-input-slot");
      const status = this.$(".place-search-status");
      let autocompleteEl = null;

      const openPopover = async () => {
        popover.hidden = false;
        if (autocompleteEl) {
          autocompleteEl.focus?.();
          return;
        }
        try {
          const maps = await loadGoogleMaps();
          autocompleteEl = new maps.places.PlaceAutocompleteElement();
          inputSlot.appendChild(autocompleteEl);
          autocompleteEl.focus?.();
          autocompleteEl.addEventListener("gmp-select", async ({ placePrediction }) => {
            const place = placePrediction.toPlace();
            await place.fetchFields({ fields: ["displayName"] });
            if (this.places.some((p) => p.placeId === place.id)) {
              status.textContent = t.t("cockpit.galleries.editor.placeAlready").replace("{place}", place.displayName || place.id);
              return;
            }
            this.places.push({ placeId: place.id, label: place.displayName || place.id });
            status.textContent = t.t("cockpit.galleries.editor.placeAdded").replace("{place}", place.displayName || place.id);
            this.#refreshPlaceList();
          });
        } catch (err) {
          status.textContent = t.t("cockpit.galleries.editor.placeSearchUnavailable").replace("{error}", err.message);
        }
      };
      const closePopover = () => {
        popover.hidden = true;
      };

      this.listen(searchToggle, "click", () => (popover.hidden ? openPopover() : closePopover()));
      this.listen(this.$(".place-search-done"), "click", closePopover);
    }

    // Upload each picked batch immediately (like the hero image upload), so
    // selecting several folders one after another just means several uploads
    // instead of one file input silently replacing the last selection.
    //
    // A single selection is itself split into small sub-batches and uploaded
    // sequentially: the backend converts each image synchronously (sharp
    // resize + WebP encode) within the request, so one request holding e.g.
    // 180 originals can run long enough to hit the reverse proxy's read
    // timeout and 503 partway through. Batches are capped by both file count
    // and total bytes (not count alone) — individual photos can be up to
    // 60MB, so a handful of large ones can add up to the reverse proxy's
    // client_max_body_size just as easily as many small ones.
    const UPLOAD_BATCH_MAX_FILES = 40;
    const UPLOAD_BATCH_MAX_BYTES = 400 * 1024 * 1024; // headroom under the proxy's 500MB cap
    const buildBatches = (files) => {
      const batches = [];
      let current = [];
      let currentBytes = 0;
      for (const f of files) {
        if (current.length && (current.length >= UPLOAD_BATCH_MAX_FILES || currentBytes + f.size > UPLOAD_BATCH_MAX_BYTES)) {
          batches.push(current);
          current = [];
          currentBytes = 0;
        }
        current.push(f);
        currentBytes += f.size;
      }
      if (current.length) batches.push(current);
      return batches;
    };

    this.listen(this.$('input[type="file"]'), "change", async (e) => {
      const picked = Array.from(e.target.files || []);
      if (!picked.length) return;
      const input = e.target;
      const label = this.$(".editor-file");
      const labelText = label.firstChild;
      const originalLabel = labelText.textContent;
      const progressEl = this.$(".editor-upload-progress");
      const progressBar = this.$(".editor-upload-progress__bar");
      input.disabled = true;
      errorEl.textContent = "";
      progressEl.hidden = false;
      progressBar.style.width = "0%";

      const batches = buildBatches(picked);
      const totalBytes = picked.reduce((sum, f) => sum + f.size, 0);
      let doneFiles = 0;
      let doneBytes = 0;

      try {
        for (const batch of batches) {
          const batchBytes = batch.reduce((sum, f) => sum + f.size, 0);
          const fd = new FormData();
          batch.forEach((f) => fd.append("images", f));
          const res = await api.postFormWithProgress(
            `/api/post/upload/${encodeURIComponent(this.galleryId)}`,
            fd,
            (loaded) => {
              const pct = totalBytes ? Math.min(100, Math.round(((doneBytes + loaded) / totalBytes) * 100)) : 0;
              progressBar.style.width = `${pct}%`;
              labelText.textContent =
                batches.length > 1
                  ? t
                      .t("common.uploadingBatch")
                      .replace("{pct}", pct)
                      .replace("{done}", doneFiles)
                      .replace("{total}", picked.length)
                  : t.t("common.uploading").replace("{pct}", pct);
            }
          );
          doneBytes += batchBytes;
          doneFiles += batch.length;
          for (const name of res?.uploadedImages || []) {
            if (!this.existingImages.includes(name)) this.existingImages.push(name);
            this.addedThisSession.add(name);
          }
        }
        this.#syncDataFromForm();
        this.setStateKeepingScroll({}, ".modal-card"); // rebuilds the form fresh, so no manual reset needed on success
      } catch (err) {
        errorEl.textContent = err.message || t.t("cockpit.galleries.editor.errUpload");
        input.disabled = false;
        labelText.textContent = originalLabel;
        progressEl.hidden = true;
      }
    });

    // Toggle removal of an image (existing or uploaded this session alike —
    // actual deletion happens via imagesToKeep reconciliation on save).
    this.$$(".editor-thumb__toggle").forEach((btn) => {
      this.listen(btn, "click", () => {
        const name = btn.getAttribute("data-name");
        if (this.removed.has(name)) this.removed.delete(name);
        else this.removed.add(name);
        // Removing the current title image clears the selection rather than
        // silently keeping a reference to an image that won't exist after save.
        if (this.removed.has(name) && this.data.titleImage === name) this.data.titleImage = null;
        this.#reflectImageState();
      });
    });

    // Pick which image is used as the gallery's title/cover image.
    this.$$(".editor-thumb__star").forEach((btn) => {
      this.listen(btn, "click", () => {
        const name = btn.getAttribute("data-name");
        this.data.titleImage = this.data.titleImage === name ? null : name;
        this.#reflectImageState();
      });
    });

    const delBtn = this.$('[data-action="delete"]');
    if (delBtn) this.listen(delBtn, "click", () => this.#delete(errorEl));

    this.listen(this.$(".editor-form"), "submit", (e) => {
      e.preventDefault();
      this.#save(errorEl);
    });
  }

  /**
   * Repaint the thumbnails from `removed` / `data.titleImage`.
   *
   * A targeted DOM patch rather than a full setState() re-render, for the same
   * reason as the place list below: re-rendering replaces the whole modal,
   * which resets the card's scroll position and drops the admin back at the top
   * of a long form every time they cross out one photo.
   */
  #reflectImageState() {
    for (const thumb of this.$$(".editor-thumb")) {
      const name = thumb.getAttribute("data-name");
      const isRemoved = this.removed.has(name);
      const isTitle = this.data.titleImage === name;
      thumb.classList.toggle("editor-thumb--removed", isRemoved);
      const toggle = thumb.querySelector(".editor-thumb__toggle");
      if (toggle) toggle.textContent = isRemoved ? "↺" : "×";
      const star = thumb.querySelector(".editor-thumb__star");
      if (star) {
        star.classList.toggle("editor-thumb__star--active", isTitle);
        star.title = isTitle ? t.t("cockpit.galleries.editor.titleImage") : t.t("cockpit.galleries.editor.setTitleImage");
      }
    }
  }

  /**
   * The tag field is a child component, not a form input: it owns its chips and
   * writes back through onChange, so `this.data.tags` is always current without
   * #syncDataFromForm having to read it. Re-mounted on every re-render like the
   * map preview — setState() tears children down (Component._teardown).
   *
   * `vocabulary` is handed down from the controller rather than fetched here:
   * the admin's gallery overview already carries every tag in use, so a lookup
   * endpoint would just re-ask for data sitting in the manager.
   */
  #mountTagInput() {
    const slot = this.$(".tag-input-slot");
    if (!slot) return;
    this.mountChild(
      TagInput,
      {
        tags: this.data.tags,
        vocabulary: this.props.vocabulary || [],
        onChange: (tags) => {
          this.data.tags = tags;
        },
      },
      slot
    );
  }

  // Rebuilds the place <ol> and the numbered preview map from this.places.
  // A targeted DOM patch rather than a full setState() re-render, so adding
  // several places in a row doesn't close the search popover or tear down
  // its live Autocomplete widget in between.
  #refreshPlaceList() {
    const list = this.$(".place-list");
    if (list) {
      list.innerHTML = this.places.length
        ? this.places
            .map(
              (p, i) => `
          <li class="place-list__item">
            <span class="place-list__marker">${i + 1}</span>
            <span class="place-list__label">${Component.escape(p.label)}</span>
            <button type="button" class="place-list__remove" data-index="${i}" title="${Component.escape(t.t("common.remove"))}">×</button>
          </li>`
            )
            .join("")
        : `<li class="place-list__empty">${Component.escape(t.t("cockpit.galleries.editor.placeEmpty"))}</li>`;
      list.querySelectorAll(".place-list__remove").forEach((btn) => {
        this.listen(btn, "click", () => {
          this.places.splice(Number(btn.getAttribute("data-index")), 1);
          this.#refreshPlaceList();
        });
      });
    }
    this.#refreshMapPreview();
  }

  #refreshMapPreview() {
    const slot = this.$(".map-preview-slot");
    if (!slot) return;
    if (this.mapPreview) {
      this.children.delete(this.mapPreview);
      this.mapPreview.unmount();
    }
    this.mapPreview = null;
    this.mountChild(
      MapView,
      { placeIds: this.places.map((p) => p.placeId), zoomLevel: Number(this.data.zoomLevel) || 8, numbered: true },
      slot
    ).then((child) => (this.mapPreview = child));
  }

  // Existing galleries only ever persisted raw place ids - look up real
  // display names once after mount so the list isn't just a wall of ids.
  async #resolveExistingLabels() {
    if (!hasMaps() || !this.places.length) return;
    try {
      const maps = await loadGoogleMaps();
      const service = new maps.places.PlacesService(document.createElement("div"));
      await Promise.all(
        this.places.map(
          (p) =>
            new Promise((resolve) => {
              service.getDetails({ placeId: p.placeId, fields: ["name"] }, (place, status) => {
                if (status === "OK" && place?.name) p.label = place.name;
                resolve();
              });
            })
        )
      );
      this.#refreshPlaceList();
    } catch {
      // Labels stay as raw place ids if lookup fails - not fatal.
    }
  }

  // setState() rebuilds the form from this.data, so anything typed since mount
  // must be captured first or it's lost the next time a thumbnail is added/removed.
  #syncDataFromForm() {
    const form = this.$(".editor-form");
    if (!form) return;
    Object.assign(this.data, {
      date: form.date.value,
      type: form.type.value,
      accessType: Number(form.accessType.value),
      zoomLevel: form.zoomLevel.value,
      en_title: form.en_title.value,
      en_subTitle: form.en_subTitle.value,
      en_text: form.en_text.value,
      de_title: form.de_title.value,
      de_subTitle: form.de_subTitle.value,
      de_text: form.de_text.value,
      // tags are not a form field — TagInput owns them and writes straight to
      // this.data.tags via its onChange (see #mountTagInput).
    });
  }

  #collect(form) {
    const d = this.data;
    const fd = new FormData();
    fd.append("id", this.galleryId);
    fd.append("date", form.date.value);
    fd.append("reverse", "false");
    fd.append("type", form.type.value);
    fd.append("accessType", form.accessType.value);
    fd.append("en_title", form.en_title.value);
    fd.append("en_subTitle", form.en_subTitle.value);
    fd.append("en_text", form.en_text.value);
    fd.append("de_title", form.de_title.value);
    fd.append("de_subTitle", form.de_subTitle.value);
    fd.append("de_text", form.de_text.value);
    fd.append("mapId", d.mapId);
    fd.append("mapPlaceId", JSON.stringify(this.places.map((p) => p.placeId)));
    fd.append("tags", JSON.stringify(d.tags));
    fd.append("zoomLevel", form.zoomLevel.value || "8");
    // Images are already on disk (uploaded immediately, see the file input
    // handler in onMounted) — save only needs to say which names to keep.
    const keep = this.existingImages.filter((n) => !this.removed.has(n));
    fd.append("imagesToKeep", JSON.stringify(keep));
    if (d.titleImage) fd.append("titleImage", d.titleImage);
    return fd;
  }

  async #save(errorEl) {
    const form = this.$(".editor-form");
    const submit = this.$('button[type="submit"]');
    errorEl.textContent = "";
    submit.disabled = true;
    submit.textContent = t.t("cockpit.galleries.editor.saving");
    try {
      await api.post(`/api/post/save/${encodeURIComponent(this.galleryId)}`, this.#collect(form), { isForm: true });
      this.finalized = true;
      this.props.onDone?.();
      // `finalized` is already set, so this closes the overlay without
      // triggering onBeforeUnmount's orphaned-upload cleanup.
      this.unmount();
    } catch (err) {
      errorEl.textContent = err.message || t.t("cockpit.galleries.editor.errSave");
      submit.disabled = false;
      submit.textContent = t.t("common.save");
    }
  }

  async #delete(errorEl) {
    if (!window.confirm(t.t("cockpit.galleries.editor.confirmDelete"))) return;
    try {
      await api.post(`/api/post/delete/${encodeURIComponent(this.galleryId)}`);
      this.finalized = true;
      this.props.onDone?.();
      this.unmount();
    } catch (err) {
      errorEl.textContent = err.message || t.t("cockpit.galleries.editor.errDelete");
    }
  }

  onBeforeUnmount() {
    unlockScroll();
    // Images upload immediately (see the file input handler above), so a brand
    // new gallery that's closed without ever being saved would otherwise leave
    // an orphaned images/<id>/ folder with no DB row behind. Existing galleries
    // are never touched here — their images are already legitimately live.
    if (this.isNew && !this.finalized && this.existingImages.length) {
      api.del(`/api/post/upload/${encodeURIComponent(this.galleryId)}`).catch(() => {});
    }
  }
}
